import type { Location } from 'history';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useParams } from 'react-router-dom';
import { StyledPage } from './styles';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import ConfirmNavigationModal from '__components/Modals/presets/ConfirmNavigation';
import { Nav } from 'components/Navigation/NavLayout';
import { useBreakpoint } from 'app/spacing';
import { DesktopOnlyButton } from '__pages/Common/styles/ControlBar';
import { MobileFallback } from 'pages/Common/MobileFallback';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { Servers } from './subpages/Servers';
import { MCPServerDetail } from './subpages/ServerDetail';
import { DeleteServerButton } from './subpages/ServerDetail/DeleteServerButton';
import {
  MCPServerToolbarProvider,
  useSaveAction,
} from './subpages/ServerDetail/ToolbarContext';
import {
  MCPServerDetailProvider,
  useMCPServerDetailPage,
} from './subpages/ServerDetail/context';

const Navigation = () => {
  const { t } = useTranslation();
  const { mcpServerId, subpage } = useParams<{
    mcpServerId: string;
    subpage?: string;
  }>();
  const saveAction = useSaveAction();
  const { server, subroutes } = useMCPServerDetailPage();
  const name = server?.name ?? '';

  const hasUnsavedChanges = !!saveAction && !saveAction.disabled;
  const currentPath = ['/mcp-servers', mcpServerId, subpage]
    .filter(Boolean)
    .join('/');

  return (
    <Nav.Container>
      <Nav.TopNavRow>
        <Nav.TopNavLeftContainer>
          <Nav.TopNavBackButton
            path="/mcp-servers"
            label={t('back to MCP servers')}
          />
        </Nav.TopNavLeftContainer>
        <Nav.TopNavCenterContainer>
          <Nav.TopNavHeader label={name} />
        </Nav.TopNavCenterContainer>
        <Nav.TopNavRightContainer>
          <DeleteServerButton />
          {saveAction ? (
            <DesktopOnlyButton
              onClick={saveAction.onSave}
              disabled={saveAction.disabled}
              data-qa-id="mcp-server-settings-save-button"
            >
              {t('Save')}
            </DesktopOnlyButton>
          ) : null}
        </Nav.TopNavRightContainer>
      </Nav.TopNavRow>
      <Nav.SubNavigationRow>
        {subroutes.map((route, i, arr) => (
          <Nav.SubNavigationLink
            key={route.path}
            route={route}
            isLast={i === arr.length - 1}
            numberOfLinks={arr.length}
            params={{ subpage }}
          />
        ))}
      </Nav.SubNavigationRow>
      <ConfirmNavigationModal
        when={hasUnsavedChanges}
        whenCondition={({ location }: { location: Location }) => {
          return location.pathname !== currentPath;
        }}
      />
    </Nav.Container>
  );
};

export const MCPServerSettingsPage = () => {
  const { t } = useTranslation();

  useSetTitleOnLoad(t('MCP Servers'));

  const isMobile = useBreakpoint();

  return (
    <MCPServerDetailProvider>
      <MCPServerToolbarProvider>
        <StyledPage>
          <Route
            path="/mcp-servers/:mcpServerId/:subpage?"
            render={() => <Navigation />}
          />
          <PageSizing>
            <ContentWidth>
              <MobileFallback isMobile={isMobile}>
                <Switch>
                  <Route exact path="/mcp-servers" render={() => <Servers />} />
                  <Route
                    path="/mcp-servers/:mcpServerId/:subpage?"
                    render={() => <MCPServerDetail />}
                  />
                </Switch>
              </MobileFallback>
            </ContentWidth>
          </PageSizing>
        </StyledPage>
      </MCPServerToolbarProvider>
    </MCPServerDetailProvider>
  );
};
